import React, { useEffect, useState, useContext } from "react";
import { Dialog } from "@headlessui/react";
import { X } from "lucide-react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { AuthContext } from "react-oauth2-code-pkce";
import FamilyHomeTabs from "./FamilyHomeTabs";
import { motion } from "framer-motion";
import imageCompression from "browser-image-compression";
import { Home, CalendarDays, Images, Users, TreePine } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";


import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  subMonths,
  addMonths,
} from "date-fns";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Professional color palette
const eventColors = {
  BIRTHDAY: "bg-blue-600",
  ANNIVERSARY: "bg-green-500",
  OTHER: "bg-purple-500",
  DEFAULT: "bg-gray-400",
};

export default function FamilyEventCalendar() {
  const { id } = useParams();
  const { token } = useContext(AuthContext);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    visible: false,
    mode: "add",
    date: null,
    title: "",
    eventType: "",
    customEventType: "",
    location: "",
    description: "",
    eventId: null,
    eventsForDay: [],
  });
  const [avatar, setAvatar] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
const [user, setUser] = useState(null);
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUser(decoded);
        console.log("Decoded user info:", decoded);
      } catch (err) {
        console.error("Invalid token:", err);
      }
    }
  }, [token]);
  // Fetch events
  useEffect(() => {
    if (!id || !token) return;

    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/family/${id}/events`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const fetchedEvents = res.data.data || [];
        const grouped = {};
        fetchedEvents.forEach((ev) => {
          const key = format(new Date(ev.eventDateTime.value), "yyyy-MM-dd");
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(ev);
        });
        setEvents(grouped);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      }
    };

    fetchEvents();
  }, [id, token]);


// Add state for input errors
const [errors, setErrors] = useState({
  title: "",
  eventType: "",
});
  const addEvent = async () => {
    // Validate mandatory fields
   let hasError = false;
     const newErrors = { title: "", eventType: "" };

     if (!modalInfo.title.trim()) {
       newErrors.title = "Event title is required.";
       hasError = true;
     }
     if (!modalInfo.eventType) {
       newErrors.eventType = "Event type is required.";
       hasError = true;
     }

     setErrors(newErrors);
     if (hasError) return;

    let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone === "Asia/Calcutta") timeZone = "Asia/Kolkata";

    const localizedDate = {
      localized: new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(new Date(modalInfo.date)),
      value: modalInfo.date,
      culture: "en-US",
      timeZone,
      raw: new Date(modalInfo.date).getTime(),
    };

    const formData = new FormData();
    if (modalInfo.eventId !== null) formData.append("eventId", Number(modalInfo.eventId));
    formData.append("familyId", id);
    formData.append("title", modalInfo.title);
    formData.append("eventType", modalInfo.eventType?.toUpperCase());
    formData.append("eventDateTime.localized", localizedDate.localized);
    formData.append("eventDateTime.value", localizedDate.value);
    formData.append("eventDateTime.culture", localizedDate.culture);
    formData.append("eventDateTime.timeZone", localizedDate.timeZone);
    formData.append("eventDateTime.raw", localizedDate.raw);
    formData.append("location", modalInfo.location);
    formData.append("description", modalInfo.description);
    if (modalInfo.eventId !== null) formData.append("eventId", Number(modalInfo.eventId));
    if (avatar) formData.append("avatar", avatar);

    try {
      const res = await axios.post(`${API_BASE_URL}/family/event`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const newEvent = res.data.data || {};
      const updated = { ...events };
      const key = format(new Date(newEvent.eventDateTime.value), "yyyy-MM-dd");

      if (!updated[key]) updated[key] = [];

      // Replace event if editing
      if (modalInfo.eventId !== null) {
        updated[key] = updated[key].map(ev =>
          ev.eventId === newEvent.eventId ? newEvent : ev
        );
      } else {
        updated[key].push(newEvent); // for new events
      }

      setEvents(updated);
      resetModal();
    } catch (err) {
      console.error("Failed to add event:", err);
      toast.error("Failed to add event. Please try again.");
    }
  };
const openEditEventModal = (event) => {
  setModalInfo({
    visible: true,
    mode: "edit",
    date: format(new Date(event.eventDateTime.value), "yyyy-MM-dd"),
    title: event.title || "",
    eventType: event.eventType?.toUpperCase() || "",
    customEventType: event.customEventType || "",
    location: event.location || "",
    description: event.description || "",
    eventId: event.eventId,
    eventsForDay: [],

  });
  // Set the existing image URL
    setPreviewUrl(event.imageUrl ? event.imageUrl : null);
    setAvatar(null); // keep null unless user uploads new image
};

  const resetModal = () => {
    setModalInfo({
      visible: false,
      mode: "add",
      date: null,
      title: "",
      eventType: "",
      customEventType: "",
      location: "",
      description: "",
      eventId: null,
      eventsForDay: [],
    });
    setAvatar(null);
    setPreviewUrl(null);
  };

  const daysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    const days = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };


  const openAddEventModal = (date) => {
    setModalInfo({
      visible: true,
      mode: "add",
      date: format(date, "yyyy-MM-dd"),
      title: "",
      eventType: "",
      customEventType: "",
      location: "",
      description: "",
      eventId: null,
      eventsForDay: [],
    });
  };

  const [isCompressing, setIsCompressing] = useState(false);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsCompressing(true); // block UI
      if (file.size > 1024 * 1024) {
        toast.success("Your image is larger than 1MB and will be compressed automatically.");
      }

      const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);

      const preview = URL.createObjectURL(compressedFile);
      setPreviewUrl(preview);
      setAvatar(compressedFile);
    } catch (error) {
      toast.error("Image compression failed. Please try again.");
    } finally {
      setIsCompressing(false); // unblock UI
    }
  };


  const deleteEvent = async () => {
    if (!modalInfo.eventId) return;

    try {
      await axios.delete(`${API_BASE_URL}/family/${modalInfo.eventId}/event`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      // Remove the event from state
      const key = format(new Date(modalInfo.date), "yyyy-MM-dd");
      const updated = { ...events };
      updated[key] = updated[key].filter(ev => ev.eventId !== modalInfo.eventId);

      setEvents(updated);
      resetModal();
      toast.success("Event deleted successfully!");
    } catch (err) {
      console.error("Failed to delete event:", err);
      toast.error("Failed to delete event. Please try again.");
    }
  };

const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth < 640);
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  const openViewEventsModal = (date, eventsForDay) => {
    setModalInfo({
      visible: true,
      mode: "view",
      date: format(date, "yyyy-MM-dd"),
      eventsForDay,
    });
  };

  const changeMonth = (direction) => {
    setCurrentDate((prev) => (direction === "prev" ? subMonths(prev, 1) : addMonths(prev, 1)));
  };

  return (
    <>
    
    <div className="flex justify-center bg-gray-50 min-h-screen px-4">
      <div className="bg-white rounded-2xl shadow-lg px-4 py-6 mb-6 w-full max-w-6xl">
       <div className="flex items-center gap-3 mb-4 sm:mb-0">
          <div className="p-3 bg-purple-100 rounded-2xl">
            <CalendarDays className="text-purple-700 w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Family Calendar</h1>
        </div>

      {/* Calendar Card */}
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-6xl p-6">
        {/* Month Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-3 sm:space-y-0">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => changeMonth("prev")}
            className="flex items-center justify-center gap-2 text-sm font-semibold
                       bg-gradient-to-r from-purple-600 to-purple-700 text-white
                       px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300
                       w-full sm:w-auto"
          >
            Previous
          </motion.button>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 text-center">
            {format(currentDate, "MMMM yyyy")}
          </h2>
         <motion.button
           whileHover={{ scale: 1.05 }}
           whileTap={{ scale: 0.95 }}
           onClick={() => changeMonth("next")}
           className="flex items-center justify-center gap-2 text-sm font-semibold
                      bg-gradient-to-r from-purple-600 to-purple-700 text-white
                      px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300
                      w-full sm:w-auto"
         >
           Next
         </motion.button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 xs:grid-cols-7 sm:grid-cols-7 gap-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="text-center font-semibold text-gray-600 py-2 text-xs sm:text-sm md:text-base"
            >
              {day}
            </div>
          ))}

          {daysInMonth().map((day, idx) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());
            const dayKey = format(day, "yyyy-MM-dd");
            const dayEvents = events[dayKey] || [];
            const maxVisible = 3;

            return (
              <div
                key={idx}
                onClick={() => openAddEventModal(day)}
                className={`aspect-square border rounded-lg flex flex-col items-start p-2 cursor-pointer transition hover:shadow-md ${
                  isCurrentMonth ? "bg-white" : "bg-gray-100"
                } ${isToday ? "border-blue-600" : "border-gray-300"}`}
              >
                <span
                  className={`text-[10px] sm:text-xs md:text-sm font-medium ${
                    isCurrentMonth ? "text-gray-900" : "text-gray-400"
                  }`}
                >
                  {format(day, "d")}
                </span>

                {/* Events */}
                <div className="w-full mt-1 flex-1 flex flex-col">
                  <div className="flex-1 space-y-1 overflow-hidden">
                    {dayEvents.slice(0, maxVisible).map((ev, idx) => {
                      const colorClass = eventColors[ev.eventType?.toUpperCase()] || eventColors.DEFAULT;
                      return (
                        <div
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation(); // prevent add modal
                            setModalInfo({
                              visible: true,
                              mode: "edit",
                              date: format(new Date(ev.eventDateTime.value), "yyyy-MM-dd"),
                              title: ev.title,
                              eventType: ev.eventType?.toUpperCase(),
                              customEventType: ev.customEventType || "",
                              location: ev.location || "",
                              description: ev.description || "",
                              eventId: ev.eventId, // important for update
                              eventsForDay: [], // optional
                              createdBy: ev.createdBy,
                            });
                            setAvatar(null); // reset avatar if needed
                            setPreviewUrl(ev.imageUrl || null); // show existing image preview
                          }}
                          className={`${colorClass} text-white text-[9px] sm:text-[10px] md:text-xs px-2 py-1 rounded truncate shadow cursor-pointer`}
                        >
                          {ev.title}
                        </div>
                      );
                    })}


                  </div>
                  {dayEvents.length > maxVisible && (
                    <button
                      type="button"
                      className="mt-1 text-[9px] sm:text-[10px] md:text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-full px-2 py-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewEventsModal(day, dayEvents);
                      }}
                    >
                      +{dayEvents.length - maxVisible} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {/* Modal */}
      <Dialog
        open={modalInfo.visible}
        onClose={resetModal}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      >
       <motion.div
         initial={{ opacity: 0, y: 50 }}
         animate={{ opacity: 1, y: 0 }}
         exit={{ opacity: 0, y: 50 }}
         transition={{ duration: 0.3 }}
         className="relative w-[95%] sm:w-full max-w-md sm:max-w-lg md:max-w-md mx-auto
                    rounded-xl sm:rounded-3xl shadow-2xl overflow-y-auto max-h-[95vh]
                    bg-gradient-to-b from-white/95 to-purple-50/90 border border-purple-100
                    px-3 sm:px-5 py-4 sm:py-5 h-full sm:h-auto"
       >
          {/* Close Button */}
          <button
            onClick={resetModal}
            className="absolute top-4 right-4 text-gray-500 hover:text-purple-700 transition"
          >
            <X className="w-5 h-5" />
          </button>

          {modalInfo.mode === "add" || modalInfo.mode === "edit" ? (
            <>
              <Dialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 text-center">
                {modalInfo.mode === "edit" ? "✨✨ Event" : "✨ Add New Event"}
              </Dialog.Title>

              <p className="text-center text-sm font-medium text-purple-700 mb-4">
                {modalInfo.date}
              </p>

              {/* Inputs */}
              <div className="space-y-3 sm:space-y-4">
                {/* Title */}
                <input
                  type="text"
                  value={modalInfo.title}
                  onChange={(e) =>
                    setModalInfo((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Event Title"
                  className="w-full rounded-xl border border-purple-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                             text-sm sm:text-base px-4 py-2 shadow-sm transition"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}

                {/* Type */}
                <select
                  value={modalInfo.eventType}
                  onChange={(e) =>
                    setModalInfo((prev) => ({ ...prev, eventType: e.target.value }))
                  }
                  className="w-full rounded-xl border border-purple-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                             text-sm sm:text-base px-4 py-2 shadow-sm transition bg-white"
                >
                  <option value="">Select Event Type</option>
                  <option value="BIRTHDAY">🎂 Birthday</option>
                  <option value="ANNIVERSARY">💍 Anniversary</option>
                  <option value="OTHER">⭐ Other</option>
                </select>
                {errors.eventType && <p className="text-red-500 text-xs mt-1">{errors.eventType}</p>}

                {modalInfo.eventType?.toUpperCase() === "OTHER" && (
                  <input
                    type="text"
                    value={modalInfo.customEventType}
                    onChange={(e) =>
                      setModalInfo((prev) => ({
                        ...prev,
                        customEventType: e.target.value,
                      }))
                    }
                    placeholder="Specify Event Type"
                    className="w-full rounded-xl border border-purple-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                               text-sm px-4 py-2 shadow-sm transition"
                  />
                )}

                {/* Location & Description */}
                <input
                  type="text"
                  value={modalInfo.location}
                  onChange={(e) =>
                    setModalInfo((prev) => ({ ...prev, location: e.target.value }))
                  }
                  placeholder="📍 Location"
                  className="w-full rounded-xl border border-purple-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                             text-sm px-4 py-2 shadow-sm transition"
                />

                <textarea
                  value={modalInfo.description}
                  onChange={(e) =>
                    setModalInfo((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="📝 Description"
                  rows={3}
                  className="w-full rounded-xl border border-purple-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                             text-sm px-4 py-2 shadow-sm transition resize-none"
                />

                {/* Image Upload */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <input
                    type="file"
                    id="avatarUpload"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  {(modalInfo.mode === "add" || (modalInfo.mode === "edit" && modalInfo.createdBy === user?.sub)) && (
                    <motion.label
                      htmlFor="avatarUpload"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="cursor-pointer bg-gradient-to-r from-purple-600 to-purple-700
                                 text-white text-sm font-semibold px-4 py-2 rounded-full
                                 shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto text-center"
                    >
                      Browse Image
                    </motion.label>
                  )}

                  {previewUrl ? (
                    <div
                      className={`relative overflow-hidden ${
                        (modalInfo.mode === "add" || modalInfo.mode === "edit") && modalInfo.createdBy === user?.sub
                          ? "w-16 h-16 sm:w-20 sm:h-20 rounded-lg sm:ml-3"
                          : "w-full max-w-xs sm:max-w-sm h-44 sm:h-52 rounded-2xl mx-auto"
                      }`}
                    >
                      {/* Blurred background */}
                      <img
                        src={previewUrl}
                        alt="blur-bg"
                        className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-70"
                      />

                      {/* Main image — fit */}
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="relative z-10 w-full h-full object-contain p-2 rounded-lg"
                      />
                    </div>
                  ) : (
                    <div
                      className={`${
                        (modalInfo.mode === "add" || modalInfo.mode === "edit") && modalInfo.createdBy === user?.sub
                          ? "w-16 h-16 sm:w-20 sm:h-20 rounded-lg border-2 border-dashed border-purple-300 flex items-center justify-center text-xs text-gray-400 sm:ml-3"
                          : "w-full max-w-xs sm:max-w-sm h-44 sm:h-52 rounded-2xl border-2 border-dashed border-purple-300 flex items-center justify-center text-sm text-gray-400 mx-auto"
                      }`}
                    >
                      No Image
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                {(modalInfo.mode === "add" || modalInfo.createdBy === user?.sub) && (
                  <motion.button
                    whileHover={{ scale: isCompressing ? 1 : 1.05 }}
                    whileTap={{ scale: isCompressing ? 1 : 0.95 }}
                    onClick={addEvent}
                    disabled={isCompressing}
                    className={`w-full font-semibold text-white text-sm py-3 rounded-full
                      bg-gradient-to-r from-purple-600 to-purple-700 shadow-md hover:shadow-lg transition-all duration-300
                      ${isCompressing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isCompressing
                      ? "Compressing Image..."
                      : modalInfo.mode === "edit"
                      ? "💾 Update Event"
                      : "+ Add Event"}
                  </motion.button>
                )}

                {modalInfo.mode === "edit" && modalInfo.createdBy === user?.sub && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setConfirmDelete(true)}
                    className="w-full font-semibold text-white text-sm py-3 rounded-full bg-red-600 shadow-md hover:bg-red-700 transition-all duration-300"
                  >
                    🗑 Delete Event
                  </motion.button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* View Mode */}
              <Dialog.Title className="text-xl font-bold text-gray-900 mb-4 text-center">
                📅 Events on {modalInfo.date}
              </Dialog.Title>

              <div className="space-y-3">
                {modalInfo.eventsForDay.map((ev, idx) => {
                  const colorClass = eventColors[ev.eventType?.toUpperCase()] || eventColors.DEFAULT;
                  return (
                    <div
                      key={idx}
                      onClick={() => openEditEventModal(ev)}
                      className={`${colorClass} p-3 rounded-xl text-white shadow-md cursor-pointer hover:shadow-lg transition`}
                    >
                      <div className="font-semibold">{ev.title}</div>
                      {ev.location && <div className="text-sm opacity-90">{ev.location}</div>}
                      {ev.description && <div className="text-xs opacity-80 mt-1">{ev.description}</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </motion.div>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-sm mx-auto rounded-3xl shadow-2xl overflow-hidden bg-white p-6"
        >
          <Dialog.Title className="text-lg font-bold text-gray-900 mb-4 text-center">
            ⚠️ Confirm Delete
          </Dialog.Title>
          <p className="text-center text-sm text-gray-700 mb-6">
            Are you sure you want to delete this event? This action cannot be undone.
          </p>
          <div className="flex justify-between gap-4">
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                deleteEvent();
                setConfirmDelete(false);
              }}
              className="flex-1 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition"
            >
              Delete
            </button>
          </div>
        </motion.div>
      </Dialog>

    {/* Toast — moved outside Dialog */}

    </div></div>
    </>
  );
}
